import * as React from 'react'
import { Link } from 'react-router-dom'
import {
  BookOpen,
  CheckCircle2,
  Circle,
  Clock,
  EyeOff,
  MessageCircle,
  MonitorPlay,
  Plus,
  Presentation,
  RotateCcw,
  Users,
  Wrench,
} from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDateTime } from '@/lib/format'
import type { Activity, ActivityCompletion, ActivityKind, ActivityStatus, CommonGoal } from '@/lib/types'
import { goalIcon } from '@/lib/goal-icons'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { ActivityEditor } from '@/pages/activity-editor'

export const KIND_META: Record<ActivityKind, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  presentation: { label: 'Presentation', icon: Presentation },
  exercise: { label: 'Exercise', icon: Wrench },
  reading: { label: 'Reading', icon: BookOpen },
  discussion: { label: 'Discussion', icon: MessageCircle },
  retro: { label: 'Retro', icon: RotateCcw },
}

export function statusVariant(status: ActivityStatus) {
  switch (status) {
    case 'complete':
      return 'success' as const
    case 'in_progress':
      return 'warning' as const
    case 'skipped':
      return 'muted' as const
    default:
      return 'outline' as const
  }
}

export function ActivitiesPage() {
  const { currentTeam, session, isLead, isCoach } = useAuth()
  const teamId = currentTeam?.id
  const userId = session?.user?.id
  const [creating, setCreating] = React.useState(false)
  const [filter, setFilter] = React.useState<'all' | 'todo' | 'done'>('all')

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!teamId)
      return {
        activities: [] as Activity[],
        completions: [] as ActivityCompletion[],
        attendance: [] as { activity_id: string; member_id: string }[],
        goalsByDeck: new Map<string, CommonGoal[]>(),
      }
    const [acts, comps, attend, goals, links] = await Promise.all([
      sb().from('activities').select('*').eq('team_id', teamId)
        .order('display_order').order('scheduled_for', { nullsFirst: false }).order('created_at'),
      sb().from('activity_completions').select('*').eq('team_id', teamId),
      // Just enough of the roll-up to show per-activity coverage in the list.
      sb().from('activity_attendance').select('activity_id, member_id').eq('team_id', teamId),
      // Which common goals a topic serves (0027). Global tables, so these two
      // reads are the same for every team and are the whole cost of the chips.
      sb().from('common_goals').select('*').eq('active', true).order('display_order'),
      sb().from('common_goal_presentations').select('goal_id, presentation_id'),
    ])
    if (acts.error) throw acts.error
    if (comps.error) throw comps.error
    if (attend.error) throw attend.error
    if (goals.error) throw goals.error
    if (links.error) throw links.error

    // presentation_id -> the goals it serves, in the catalog's own order so the
    // chips don't reshuffle between rows that share a goal.
    const catalog = (goals.data ?? []) as CommonGoal[]
    const byId = new Map(catalog.map((g) => [g.id, g]))
    const goalsByDeck = new Map<string, CommonGoal[]>()
    for (const l of (links.data ?? []) as { goal_id: string; presentation_id: string }[]) {
      const g = byId.get(l.goal_id)
      // A link to a retired goal resolves to nothing here rather than to a blank
      // chip — `active` is filtered above, and the mapping outlives it.
      if (!g) continue
      goalsByDeck.set(l.presentation_id, [...(goalsByDeck.get(l.presentation_id) ?? []), g])
    }
    for (const list of goalsByDeck.values()) {
      list.sort((a, b) => a.display_order - b.display_order)
    }

    return {
      activities: (acts.data ?? []) as Activity[],
      completions: (comps.data ?? []) as ActivityCompletion[],
      attendance: (attend.data ?? []) as { activity_id: string; member_id: string }[],
      goalsByDeck,
    }
  }, [teamId])

  const attendedByActivity = React.useMemo(() => {
    const m = new Map<string, number>()
    for (const a of data?.attendance ?? []) m.set(a.activity_id, (m.get(a.activity_id) ?? 0) + 1)
    return m
  }, [data?.attendance])

  // Only a topic backed by a deck can serve a goal: the mapping is
  // goal <-> presentation, so a hand-written topic has no association to show
  // and gets no chips rather than an empty row.
  const goalsFor = React.useCallback(
    (a: Activity): CommonGoal[] =>
      a.presentation_id ? (data?.goalsByDeck.get(a.presentation_id) ?? []) : [],
    [data?.goalsByDeck],
  )

  const myDone = React.useMemo(
    () => new Set((data?.completions ?? []).filter((c) => c.user_id === userId).map((c) => c.activity_id)),
    [data?.completions, userId],
  )

  const countsByActivity = React.useMemo(() => {
    const m = new Map<string, number>()
    for (const c of data?.completions ?? []) m.set(c.activity_id, (m.get(c.activity_id) ?? 0) + 1)
    return m
  }, [data?.completions])

  const shown = (data?.activities ?? []).filter((a) => {
    if (filter === 'todo') return !myDone.has(a.id) && a.status !== 'skipped'
    if (filter === 'done') return myDone.has(a.id)
    return true
  })

  // Only a coach ever has any of these to count: RLS filters hidden topics out
  // of the query for members and leads alike.
  const hiddenCount = (data?.activities ?? []).filter((a) => !a.visible).length

  async function toggleComplete(activity: Activity) {
    if (!userId) return
    const done = myDone.has(activity.id)
    // Optimistic: the list re-renders immediately, then reconciles on refetch.
    try {
      if (done) {
        const { error: err } = await sb()
          .from('activity_completions')
          .delete()
          .eq('activity_id', activity.id)
          .eq('user_id', userId)
        if (err) throw err
      } else {
        const { error: err } = await sb()
          .from('activity_completions')
          .insert({ activity_id: activity.id, team_id: activity.team_id })
        if (err) throw err
      }
    } finally {
      void refetch()
    }
  }

  return (
    <Page
      title="Topics"
      description="Learning sessions and the hands-on work that goes with them."
      actions={
        <>
          <Tabs value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="todo">To do</TabsTrigger>
              <TabsTrigger value="done">Done</TabsTrigger>
            </TabsList>
          </Tabs>
          {/* Authoring the curriculum is a lead's job; members present a topic
              and tick it off. RLS enforces it — this only stops the UI from
              offering a button that would 403. */}
          {isLead && (
            <Button onClick={() => setCreating(true)}>
              <Plus /> New topic
            </Button>
          )}
        </>
      }
    >
      {error && <ErrorNote error={error} />}
      {isCoach && hiddenCount > 0 && (
        <p className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <EyeOff className="h-3.5 w-3.5" />
          {hiddenCount} {hiddenCount === 1 ? 'topic is' : 'topics are'} hidden from the team.
          Presenting one makes it visible.
        </p>
      )}
      {loading && !data ? (
        <Loading />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={Presentation}
          title={filter === 'all' ? 'No topics yet' : 'Nothing here'}
          description={
            filter === 'all'
              ? isLead
                ? 'Add the presentations, exercises, and readings that make up this accelerator.'
                : 'Your team lead or DPL coach hasn’t published any topics yet.'
              : filter === 'todo'
                ? 'You’ve completed everything on the list.'
                : 'You haven’t marked anything complete yet.'
          }
          action={
            filter === 'all' &&
            isLead && (
              <Button onClick={() => setCreating(true)}>
                <Plus /> Add the first topic
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-2.5">
          {shown.map((a) => {
            const meta = KIND_META[a.kind]
            const done = myDone.has(a.id)
            const others = countsByActivity.get(a.id) ?? 0
            const attended = attendedByActivity.get(a.id) ?? 0
            return (
              <Card key={a.id} className="transition-colors hover:border-primary/40">
                <CardContent className="flex items-start gap-3 p-4">
                  <button
                    type="button"
                    onClick={() => void toggleComplete(a)}
                    className="mt-0.5 shrink-0 rounded-full text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={done ? `Mark ${a.title} not done` : `Mark ${a.title} done`}
                    aria-pressed={done}
                  >
                    {done ? (
                      <CheckCircle2 className="h-5 w-5 text-better" />
                    ) : (
                      <Circle className="h-5 w-5" />
                    )}
                  </button>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/activities/${a.id}`}
                        className={cn(
                          'font-medium hover:underline',
                          done && 'text-muted-foreground line-through decoration-1',
                        )}
                      >
                        {a.title}
                      </Link>
                      <Badge variant="muted" className="gap-1 text-[10px]">
                        <meta.icon className="h-3 w-3" />
                        {meta.label}
                      </Badge>
                      {a.presentation_id && (
                        <Badge variant="outline" className="gap-1 text-[10px]">
                          <MonitorPlay className="h-3 w-3" /> Slides
                        </Badge>
                      )}
                      {a.status !== 'planned' && (
                        <Badge variant={statusVariant(a.status)} className="text-[10px]">
                          {a.status === 'in_progress' ? 'in progress' : a.status}
                        </Badge>
                      )}
                      {/* Reaching this row at all means the viewer is a coach —
                          RLS filters hidden topics out for everyone else — so
                          this badge says "the team can't see this yet", not
                          "you can't". */}
                      {!a.visible && (
                        <Badge variant="outline" className="gap-1 text-[10px]">
                          <EyeOff className="h-3 w-3" /> Hidden
                        </Badge>
                      )}
                    </div>
                    {a.summary && (
                      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{a.summary}</p>
                    )}

                    {/* Which of DPL's common goals this topic serves (0027).
                        Its own line rather than joining the badge row above:
                        those badges describe what the topic IS (a deck, in
                        progress, hidden) and these say what it is FOR, and three
                        long-ish chips in that row pushed the status badges onto a
                        second line anyway.
                        The chip carries the catalog's short label; the tooltip
                        carries the full sentence, because "Onboarding" alone
                        doesn't say what the team is aiming at. */}
                    {goalsFor(a).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        {goalsFor(a).map((g) => {
                          const Icon = goalIcon(g.icon)
                          return (
                            <Tooltip key={g.id}>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="secondary"
                                  className="cursor-default gap-1 text-[10px] font-normal"
                                >
                                  <Icon className="h-3 w-3" />
                                  {g.short_label || g.title}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-[260px] text-xs">{g.title}</p>
                              </TooltipContent>
                            </Tooltip>
                          )
                        })}
                      </div>
                    )}
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      {a.scheduled_for && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {formatDateTime(a.scheduled_for)}
                        </span>
                      )}
                      {a.presenter && <span>Led by {a.presenter}</span>}
                      {a.duration_minutes && <span>{a.duration_minutes} min</span>}
                      {attended > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {attended} {attended === 1 ? 'person has' : 'people have'} been through it
                        </span>
                      )}
                      {/* Only worth showing when it differs from the attendance
                          number — otherwise it's the same fact twice. */}
                      {others > 0 && others !== attended && (
                        <span>{others} marked complete</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {creating && teamId && (
        <ActivityEditor
          teamId={teamId}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            void refetch()
          }}
        />
      )}
    </Page>
  )
}
